import React from "react";
import {Container, LoginButton} from "../styles/Styles";
import {SubmitHandler, useForm} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {useMutation} from "@apollo/client";
import {LOGIN} from "../components/gql/auth-gql";
import {toast} from "react-toastify";
import {useAuth} from "../context/AuthContext";
import styled from "styled-components";
import { jwtDecode } from "jwt-decode";

const LoginContainer = styled(Container)`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: #f5f5f5;
`;

const LoginForm = styled.form`
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    input {
        padding: 0.8rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1rem;
        width: 100%;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: #4CAF50;
        }
    }

    .error {
        color: #f44336;
        font-size: 0.9rem;
        margin-top: -0.5rem;
    }
`;

type Inputs = {
    email: string;
    password: string;
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Inputs>();

    const [loginMutation] = useMutation(LOGIN, {
        onCompleted: (data) => {
            if (data.login.message === "SUCCESSFUL") {
                if (!data.login.accessToken) {
                    console.error("No access token received from server");
                    toast.error("Login failed: No access token received");
                    return;
                }

                // Store token and update auth context
                login(data.login.accessToken);

                // Decode token to get user role
                const decoded: any = jwtDecode(data.login.accessToken);
                if (decoded.role.includes('ADMIN')) {
                    navigate("/admin");
                } else if (decoded.role.includes('GUIDE')) {
                    navigate("/guide");
                } else if (decoded.role.includes('COORDINATOR')) {
                    navigate("/coordinator");
                } else {
                    navigate("/dashboard");
                }
            } else {
                console.error("Login was not successful:", data.login.message);
                toast.error(`Login failed: ${data.login.message}`);
            }
        },
        onError: (error) => {
            console.error("Login error:", error);
            console.error("Network error:", error.networkError);
            console.error("GraphQL errors:", error.graphQLErrors);
            toast.error(error.graphQLErrors?.[0]?.message || "Login failed: Please check your credentials");
        }
    });

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        try {
            await loginMutation({
                variables: {
                    loginInput: {
                        email: data.email.trim(),
                        password: data.password
                    }
                }
            });
        } catch (error) {
            console.error('Login submission error:', error);
            toast.error('Failed to submit login request');
        }
    }

    return (
        <LoginContainer>
            <LoginForm onSubmit={handleSubmit(onSubmit)}>
                <h2>Login</h2>
                <input 
                    {...register("email", { 
                        required: true,
                        setValueAs: (value) => value.trim()
                    })} 
                    placeholder="Email" 
                />
                {errors.email && <span className="error">This field is required</span>}

                <input {...register("password", { required: true })} type="password" placeholder="Password" />
                {errors.password && <span className="error">This field is required</span>}

                <LoginButton type="submit">Login</LoginButton>
            </LoginForm>
        </LoginContainer>
    );
}

export default LoginPage;
